import { townsperson, } from './content.js';
/**
 * The meadow's mark of the dragon (ADR 0067, `docs/LORE.md`): after the Great
 * Scarecrow, the Ferme calcinée, where Shefira's shadow passes (the
 * `shefira-shadow` cinematic plays as `meadow-dragon-farm` is turned in),
 * the scale found in its ashes, the name the Ermite Aubépin reads in his
 * stones, then la Guetteuse Maëlle, who saw the shadow first and whom no one
 * believed, joins the Givrecoeur Company at Havrebourg.
 *
 * Part of the chain: the horse and the road west (`meadow-stable`,
 * `meadow-west`) wait on the recruit. The farm stands intact, for each
 * player, until the Great Scarecrow falls (`before` on its props, the
 * client's `BeforeTheFire`).
 */
/** The townsfolk only these quests need, added to the bestiary. */
export const MEADOW_DRAGON_NPC_IDS = ['meadow-farmer'];
const CAPTAIN = { mob: 'captain', biome: 'meadow' };
const HERMIT = { mob: 'hermit', biome: 'meadow' };
const LOOKOUT = { mob: 'lookout', biome: 'meadow' };
const FARMER = { mob: 'meadow-farmer', biome: 'meadow' };
/** Ser Givrecoeur in the Company's quarter of Havrebourg, not the camp's (ADR 0067). */
export const GIVRECOEUR = { mob: 'tutor', biome: 'meadow' };
export const MEADOW_DRAGON_QUESTS = [
    {
        id: 'meadow-dragon-farm',
        name: 'La Ferme calcinée',
        biome: 'meadow',
        level: 5,
        requires: ['meadow-scarecrow'],
        giver: LOOKOUT,
        turnIn: FARMER,
        offer: 'Cette nuit, un peu après minuit, une ombre énorme est passée au-dessus de la prairie. Plus grande que ma tour, et sans faire un bruit ! Puis le ciel est devenu rouge derrière la colline. La ferme de Gautier fume encore. Va voir s’il va bien, vite. Et chasse les renards qui traînent là-bas.',
        summary: 'Rejoindre la Ferme calcinée, chasser les renards qui fouillent ses cendres et trouver le fermier Gautier.',
        pending: 'Chasse les renards qui fouillent la ferme, puis cherche Gautier dans la cour.',
        complete: 'C’était pas un orage, j’te jure, il n’y avait pas un nuage ! Il y a eu un grand souffle de feu, un seul, et ma grange est partie en fumée. J’ai trouvé ce truc dans les cendres ce matin. C’est une écaille, et elle est encore chaude ! Prends-la, je n’en veux pas chez moi. Là ! Dans la fumée, tu l’as vue ?',
        objectives: [
            { kind: 'reach', label: 'Atteindre la Ferme calcinée', landmark: 'La Ferme calcinée' },
            {
                kind: 'kill',
                label: 'Renards calcinés chassés des cendres',
                mobs: ['fox-charred'],
                count: 6,
            },
        ],
        reward: { xp: 45, gold: 9, items: [{ templateId: 'shefira-scale', quantity: 1 }] },
    },
    {
        id: 'meadow-dragon-stones',
        name: 'Le nom dans les pierres',
        biome: 'meadow',
        level: 5,
        requires: ['meadow-dragon-farm'],
        giver: FARMER,
        turnIn: HERMIT,
        offer: 'Depuis cette nuit, les sangliers sont devenus fous, ils foncent même sur les charrettes. Aubépin, l’ermite, sait lire les vieilles pierres. Montre-lui l’écaille, il saura peut-être ce que c’est. Moi, je ne veux plus la voir.',
        summary: 'Repousser les sangliers rendus fous par l’incendie et montrer l’écaille à l’Ermite Aubépin, au Cercle des Anciens.',
        pending: 'Chasse d’abord les sangliers fous, puis porte l’écaille à l’ermite, au Cercle des Anciens, au nord de la ville.',
        complete: 'Pose-la là. Non, là, sur la pierre plate. Tu vois ce dessin gravé ? Je le nettoie depuis quarante ans sans savoir ce qu’il voulait dire. C’est un nom : Shefira. Un dragon. Elle dort enchaînée sous la glace de Givrecime, très loin au nord. Mais d’après les pierres, la chaîne lâche parfois, et elle sort la nuit pour tout brûler. Maëlle n’a pas rêvé, finalement !',
        objectives: [{ kind: 'kill', label: 'Sangliers affolés repoussés', mobs: ['boar'], count: 8 }],
        reward: { xp: 40, gold: 8 },
    },
    {
        id: 'meadow-recruit',
        name: 'Tant que le givre tient',
        biome: 'meadow',
        level: 5,
        requires: ['meadow-dragon-stones'],
        giver: LOOKOUT,
        turnIn: GIVRECOEUR,
        offer: 'Je le savais ! Ça fait six ans que je le dis, et tout le monde se moquait de moi. Le vieux chevalier, au nord de la place, il cherche des gens pour combattre ce dragon ? Alors je viens avec toi ! De là-haut, je la verrai arriver avant tout le monde. Mais d’abord, montre l’écaille à Aldric. Je veux voir sa tête.',
        summary: 'Montrer l’écaille au capitaine Aldric, puis rejoindre Ser Givrecoeur au quartier de la Compagnie, à Havrebourg.',
        pending: 'Montre l’écaille à Aldric sur la place, puis va voir Ser Givrecoeur au quartier de la Compagnie, juste au nord. Je prends mon arc et je te rejoins.',
        complete: 'Une écaille de Shefira, encore tiède ! Trois hivers que j’attends une preuve, et c’est un fermier qui la trouve dans sa grange. Ha ! Maëlle, bienvenue dans la Compagnie : tu seras nos yeux. Et toi, recrue, garde l’écaille. La prochaine fois que quelqu’un se moque de nous, tu la sors.',
        objectives: [
            {
                kind: 'talk',
                label: 'Montrer l’écaille au capitaine',
                npc: CAPTAIN,
                line: 'Elle est… chaude ? Pose-la sur la table. Non, reprends-la, reprends-la ! Bon. J’ai peut-être été un peu dur avec Givrecoeur. Va le voir. Et ne lui dis pas que j’ai eu peur d’une écaille.',
            },
        ],
        reward: { xp: 30, gold: 6 },
    },
];
export const MEADOW_DRAGON_NPCS = {
    // A farmer of the meadow, in the body of the villages' own.
    'meadow-farmer': townsperson('meadow-farmer', 'Fermier Gautier', { bodyKey: 'body.farmer' }),
};
export const MEADOW_DRAGON_KEEPERS = [
    // In the ashes of his farm (ADR 0067), in the yard before the ruins.
    { mob: 'meadow-farmer', near: 'La Ferme calcinée', dx: 0, dy: 3, facing: 'south' },
    // The foxes digging in its ashes, each player's own (ADR 0087).
    {
        mob: 'fox-charred',
        near: 'La Ferme calcinée',
        dx: 0,
        dy: -3,
        count: 4,
        radius: 5,
        during: 'meadow-dragon-farm',
    },
];
export const MEADOW_DRAGON_ITEMS = [
    {
        id: 'shefira-scale',
        name: 'Écaille de Shefira',
        // Kept, never used nor sold: the thread the Company follows (ADR 0067).
        kind: 'material',
        rarity: 'legendary',
        maxStack: 1,
        icon: 'icon.shefira-scale',
        lootable: false,
        sellPrice: 0,
    },
];
export const MEADOW_DRAGON_GREETINGS = {
    'meadow-farmer': 'Ma femme veut partir chez sa sœur, à Sylvebois. Moi, je reste. Et bonne nouvelle : trois poules sont revenues !',
    tutor: 'Il fait trop chaud dans votre vallée, je dors mal. Chez moi, à Givrecime, on dort avec la fenêtre ouverte en plein hiver.',
};
//# sourceMappingURL=meadow-dragon.js.map